import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, Navigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Plus, Ticket } from 'lucide-react'
import { getReservationList } from '../../lib/api/auth'
import { demoByEmail } from '../../lib/demo'
import { useAuth } from '../../store/authStore'
import { TicketModal } from './TicketModal'
import { PageShell } from '../../components/layout/PageShell'
import { Skeleton, ErrorState } from '../../components/ui/States'
import { ReservationCard } from './ReservationCard'
import type { Locale, ReservationListItem } from '../../lib/api/types'
import { cn } from '../../lib/cn'

type Tab = 'active' | 'archived'

export function MyBookings() {
  const { t, i18n } = useTranslation()
  const locale = (i18n.language as Locale) ?? 'it'
  const { locale: routeLocale = 'it' } = useParams()
  const status = useAuth((s) => s.status)
  const user = useAuth((s) => s.user)
  const demo = demoByEmail(user?.email)
  const [tab, setTab] = useState<Tab>('active')
  const [ticket, setTicket] = useState<ReservationListItem | null>(null)

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['reservationList', user?.email],
    queryFn: () => (demo ? Promise.resolve(demo.reservations) : getReservationList()),
    enabled: status === 'authenticated',
  })

  const active = data?.activeList ?? []
  const archived = data?.archivedList ?? []
  const list = useMemo(() => (tab === 'active' ? active : archived), [tab, active, archived])

  if (status === 'unknown')
    return (
      <PageShell desktopWide>
        <Skeleton className="h-8 w-48" />
        <div className="mt-6 space-y-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
      </PageShell>
    )
  if (status === 'guest')
    return <Navigate to={`/${routeLocale}/login?redirect=/${routeLocale}/bookings`} replace />

  const TABS: { key: Tab; label: string; count?: number }[] = [
    { key: 'active', label: t('bookings.active'), count: active.length },
    { key: 'archived', label: t('bookings.archived'), count: archived.length },
  ]

  return (
    <PageShell desktopWide>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-ink-900 sm:text-3xl">
            {t('bookings.title')}
          </h1>
          <p className="mt-1.5 text-sm text-ink-500">{t('bookings.subtitle')}</p>
        </div>
        <Link
          to={`/${routeLocale}/book`}
          className="inline-flex h-11 items-center gap-2 rounded-xl bg-brand-600 px-4 text-sm font-semibold text-white transition hover:bg-brand-700"
        >
          <Plus className="h-4 w-4" /> {t('bookings.newBooking')}
        </Link>
      </div>

      {/* tabs — one row; scroll horizontally on mobile if the labels don't fit (no wrapping) */}
      <div className="mb-5 -mx-4 overflow-x-auto px-4 [scrollbar-width:none] sm:mx-0 sm:px-0 [&::-webkit-scrollbar]:hidden">
        <div className="inline-flex gap-1 rounded-xl bg-ink-100 p-1">
          {TABS.map((tb) => (
            <button
              key={tb.key}
              onClick={() => setTab(tb.key)}
              className={cn(
                'whitespace-nowrap rounded-lg px-3.5 py-2 text-sm font-semibold transition-colors',
                tab === tb.key ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-500 hover:text-ink-800',
              )}
            >
              {tb.label}
              {tb.count != null && (
                <span className={cn('ml-1.5 text-xs', tab === tb.key ? 'text-brand-600' : 'text-ink-400')}>{tb.count}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {isError ? (
        <ErrorState onRetry={() => refetch()} />
      ) : isLoading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
      ) : list.length === 0 ? (
        <EmptyState tab={tab} routeLocale={routeLocale} />
      ) : (
        <div className="grid gap-3 lg:grid-cols-2 lg:items-start">
          {list.map((r) => (
            <ReservationCard key={r.code} r={r} locale={locale} onOpen={() => setTicket(r)} />
          ))}
        </div>
      )}

      {ticket && <TicketModal r={ticket} onClose={() => setTicket(null)} />}
    </PageShell>
  )
}

function EmptyState({ tab, routeLocale }: { tab: Tab; routeLocale: string }) {
  const { t } = useTranslation()
  const msg = tab === 'active' ? t('bookings.emptyActive') : t('bookings.emptyArchived')
  return (
    <div className="gsm-card flex flex-col items-center justify-center px-6 py-16 text-center">
      <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-ink-100 text-ink-400">
        <Ticket className="h-7 w-7" />
      </span>
      <p className="text-ink-500">{msg}</p>
      <Link
        to={`/${routeLocale}/book`}
        className="mt-5 inline-flex h-11 items-center gap-2 rounded-xl bg-brand-600 px-4 text-sm font-semibold text-white transition hover:bg-brand-700"
      >
        <Plus className="h-4 w-4" /> {t('bookings.newBooking')}
      </Link>
    </div>
  )
}
